import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Progress,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Empty,
  message,
  Card,
  Divider,
  Spin,
  Segmented,
  Skeleton,
  Input,
  Tour,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  LinkOutlined,
  LoadingOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { v2ArticlesApi } from '../../../../api';
import './step4.css';
import TipsRotator from '../../../Common/TipsRotator';
import { generationStep4_2Tips, waitingTips } from '../../../Common/tipsData';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import EnhancedTour from '../../../Common/EnhancedTour';

const { Title, Text } = Typography;

/**
 * STEP4.2: 本文生成コンポーネント
 */
const Step4_2_ContentGeneration = ({
  articleUuid,
  articleData,
  writingPlan,
  onCompleted,
  // 親から渡される進捗管理のprops
  generating,
  generationStarted,
  progress,
  currentHeading,
  currentHeadingIndex,
  totalHeadings,
  generatedContents,
  citations,
  stats,
  onStartGeneration,
  onLoadContent,
  userRole = 'user',
  isReadOnly = false,
  isGuest = false,
}) => {
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('generation_step4_2', { disabled: isGuest });
  const tourStartedRef = useRef(false);

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // State管理（ローカルUIのみ）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 右カラムの表示切り替え
  const [rightPanelView, setRightPanelView] = useState('toc');

  // 参考データの検索フィルター
  const [citationSearchQuery, setCitationSearchQuery] = useState('');

  // 現在アクティブな見出しID（スクロール位置に応じて更新）
  const [activeHeadingId, setActiveHeadingId] = useState('');

  // ハイライト表示中の引用ID
  const [highlightedCitationId, setHighlightedCitationId] = useState(null);

  // 新規生成されたコンテンツID（フェードイン用）
  const [newContentIds, setNewContentIds] = useState(new Set());

  // タイプライター効果用のstate
  const [typewriterContent, setTypewriterContent] = useState(new Map());
  const typewriterTimeoutRef = useRef(new Map());

  // Refs
  const mountedRef = useRef(true);

  // Outline data
  const outlineData = articleData?.outline?.outline_data || null;
  const workingDraft = outlineData?.working_draft || null;

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Lifecycle
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // クリーンアップ: すべてのタイムアウトをクリア
      typewriterTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
      typewriterTimeoutRef.current.clear();
    };
  }, []);

  // 自動生成開始：生成未開始かつ生成中でない場合に自動で開始（閲覧専用時は無効）
  useEffect(() => {
    if (isReadOnly) return; // ⭐ 閲覧専用時は自動生成しない
    if (!generationStarted && !generating && articleUuid && onStartGeneration) {
      // 少し遅延させて開始（UIの初期化を待つ）
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          onStartGeneration();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    generationStarted,
    generating,
    articleUuid,
    onStartGeneration,
    isReadOnly,
  ]);

  // タイプライター効果: 新しいコンテンツが追加されたときに実行
  useEffect(() => {
    if (!generating || generatedContents.length === 0) return;

    // 新しいコンテンツを検出
    const newContents = generatedContents.filter(
      content => !typewriterContent.has(content.heading_id),
    );

    if (newContents.length === 0) return;

    // 新しいコンテンツが追加された場合、進行中のタイプライターを即座に完了
    if (newContents.length > 0 && typewriterContent.size > 0) {
      // 進行中のすべてのタイマーをクリア
      typewriterTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
      typewriterTimeoutRef.current.clear();

      // 進行中のコンテンツを即座に全文表示
      setTypewriterContent(prev => {
        const newMap = new Map(prev);
        generatedContents.forEach(content => {
          if (prev.has(content.heading_id)) {
            // 既に表示中のものは全文に
            newMap.set(content.heading_id, content.content || '');
          }
        });
        return newMap;
      });
    }

    // 最新の1つだけタイプライター効果を適用
    const latestContent = newContents[newContents.length - 1];
    const contentId = latestContent.heading_id;
    const fullText = latestContent.content || '';
    let currentIndex = 0;

    // 初期状態を設定（空文字から開始）
    setTypewriterContent(prev => new Map(prev).set(contentId, ''));

    const typeNextChar = () => {
      if (currentIndex < fullText.length && mountedRef.current) {
        // 1文字追加（HTMLタグは一度に追加）
        let nextIndex = currentIndex + 1;
        if (fullText[currentIndex] === '<') {
          // HTMLタグの終了まで進める
          const closingIndex = fullText.indexOf('>', currentIndex);
          if (closingIndex !== -1) {
            nextIndex = closingIndex + 1;
          }
        }

        const displayText = fullText.substring(0, nextIndex);
        setTypewriterContent(prev => new Map(prev).set(contentId, displayText));

        currentIndex = nextIndex;

        // 次の文字へ（速度: 2.5ms/文字 = 倍速）
        const timeout = setTimeout(typeNextChar, 2.5);
        typewriterTimeoutRef.current.set(contentId, timeout);
      } else {
        // 完了: 完全なテキストを設定
        setTypewriterContent(prev => new Map(prev).set(contentId, fullText));
        typewriterTimeoutRef.current.delete(contentId);
      }
    };

    // タイプライター開始
    typeNextChar();
  }, [generatedContents, generating]);

  // 新しいコンテンツが追加されたときに自動スクロール
  useEffect(() => {
    if (!generating || generatedContents.length === 0) return;

    // 最後に追加されたコンテンツまでスクロール
    const lastContent = generatedContents[generatedContents.length - 1];
    const element = document.getElementById(lastContent.heading_id);

    if (element) {
      // 少し遅延させてDOMの更新を待つ
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [generatedContents.length, generating]);

  // ツアー開始: 本文生成完了時
  useEffect(() => {
    if (
      !tourLoading &&
      !tourStartedRef.current &&
      !generating &&
      generatedContents.length > 0 &&
      stats.total_headings > 0
    ) {
      tourStartedRef.current = true;
      // 少し遅延させてDOMの準備を待つ
      setTimeout(() => {
        // ツアーは useTutorialTour フックが自動的に開始する
      }, 1000);
    }
  }, [tourLoading, generating, generatedContents, stats]);

  // スクロール監視：現在の見出し位置を追跡
  useEffect(() => {
    const handleScroll = () => {
      if (generatedContents.length === 0) return;

      const scrollPosition = window.scrollY + 160; // ヘッダー + マージン

      // 各見出しの位置を取得（下から上へチェック）
      for (let i = generatedContents.length - 1; i >= 0; i--) {
        const content = generatedContents[i];
        const element = document.getElementById(content.heading_id);
        if (element) {
          const elementTop =
            element.getBoundingClientRect().top + window.scrollY;
          if (elementTop <= scrollPosition) {
            setActiveHeadingId(content.heading_id);
            return;
          }
        }
      }

      // 最初の見出しより上にいる場合
      if (generatedContents.length > 0) {
        setActiveHeadingId(generatedContents[0].heading_id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初期位置を設定

    return () => window.removeEventListener('scroll', handleScroll);
  }, [generatedContents]);

  // 引用番号クリック時の処理
  useEffect(() => {
    const handleCitationClick = e => {
      if (e.target.classList.contains('step4-citation-number')) {
        const citationId = parseInt(
          e.target.getAttribute('data-citation-id'),
          10,
        );
        handleCitationLinkClick(citationId);
      }
    };

    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, []);

  // 引用番号クリック時のハンドラ
  const handleCitationLinkClick = citationId => {
    // 1. 参考データビューに切り替え
    setRightPanelView('reference');

    // 2. 該当カードまでスクロール（少し遅延させてビュー切り替え後に実行）
    setTimeout(() => {
      const cardElement = document.getElementById(`citation-${citationId}`);
      if (cardElement) {
        // カスタムスムーススクロール（早めのアニメーション）
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 3. ハイライト表示
        setHighlightedCitationId(citationId);

        // 4. 3秒後にハイライトを解除（フェードアウト）
        setTimeout(() => {
          setHighlightedCitationId(null);
        }, 3000);
      }
    }, 100);
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Helper Functions
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  // 簡易Markdownレンダリング（タイプライター効果付き）
  const renderMarkdown = (text, contentId) => {
    if (!text) return null;

    // タイプライター効果中の場合は、部分的なテキストを使用
    const displayText =
      generating && typewriterContent.has(contentId)
        ? typewriterContent.get(contentId)
        : text;

    if (!displayText) return null;

    // 疑似タグ → HTML 変換（マークダウンより先に処理）
    let html = displayText;

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Markdownフォーマット変換（独自情報のテキストエディタ用）
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // 画像: ![alt](url) → <img src="url" alt="alt" />
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />',
    );

    // リンク: [text](url) → <a href="url">text</a>
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
    );

    // 見出し: # text → <h2>text</h2> (複数の#にも対応)
    html = html.replace(
      /^#{1}\s+(.+)$/gm,
      '<h2 style="margin: 20px 0 12px; font-size: 20px; font-weight: 600;">$1</h2>',
    );
    html = html.replace(
      /^#{2}\s+(.+)$/gm,
      '<h3 style="margin: 16px 0 10px; font-size: 18px; font-weight: 600;">$1</h3>',
    );
    html = html.replace(
      /^#{3}\s+(.+)$/gm,
      '<h4 style="margin: 12px 0 8px; font-size: 16px; font-weight: 600;">$1</h4>',
    );

    // 箇条書き: - item → <ul><li>item</li></ul>
    // 連続する - をグループ化して<ul>で囲む
    html = html.replace(/(?:^- .+$\n?)+/gm, match => {
      const items = match
        .trim()
        .split('\n')
        .map(line => {
          const content = line.replace(/^-\s+/, '');
          return `<li>${content}</li>`;
        })
        .join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${items}</ul>`;
    });

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // カスタムタグ変換
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // タグ欠損の自動補完
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // 閉じタグの欠損を補完（[TAG]...text[TAG] → [TAG]...text[/TAG]）
    const fixMissingClosingTags = (text, tagName) => {
      const openTag = `[${tagName}]`;
      const closeTag = `[/${tagName}]`;
      const regex = new RegExp(`\\[${tagName}\\]([^[]*?)\\[${tagName}\\]`, 'g');
      return text.replace(regex, `${openTag}$1${closeTag}`);
    };

    // 開きタグの欠損を補完（[/TAG]...text[/TAG] → [TAG]...text[/TAG]）
    const fixMissingOpeningTags = (text, tagName) => {
      const openTag = `[${tagName}]`;
      const closeTag = `[/${tagName}]`;
      const regex = new RegExp(
        `\\[/${tagName}\\]([^[]*?)\\[/${tagName}\\]`,
        'g',
      );
      return text.replace(regex, `${openTag}$1${closeTag}`);
    };

    // 各タグの欠損を修正
    const tagsToFix = ['TD', 'TH', 'TR', 'LI', 'B', 'H2', 'H3', 'H4'];
    tagsToFix.forEach(tag => {
      html = fixMissingClosingTags(html, tag);
      html = fixMissingOpeningTags(html, tag);
    });

    // 特殊ケース: [TD]text[TD] のようなパターン（連続する同じタグ）を補完
    // これは複数のセルが連続している場合に発生する可能性がある
    html = html.replace(/\[TD\]([^[]*?)\[TD\]/g, '[TD]$1[/TD][TD]');
    html = html.replace(/\[TH\]([^[]*?)\[TH\]/g, '[TH]$1[/TH][TH]');
    html = html.replace(/\[LI\]([^[]*?)\[LI\]/g, '[LI]$1[/LI][LI]');

    // タグ直前・直後の改行を先に削除（\nをまだ<br/>に変換する前に処理）
    // 箇条書き
    html = html.replace(/\n+(\[UL\])/gi, '$1');
    html = html.replace(/(\[\/UL\])\n+/gi, '$1');
    // テーブル
    html = html.replace(/\n+(\[TABLE\])/gi, '$1');
    html = html.replace(/(\[\/TABLE\])\n+/gi, '$1');
    // H2見出し
    html = html.replace(/\n+(\[H2\])/gi, '$1');
    html = html.replace(/(\[\/H2\])\n+/gi, '$1');
    // H3見出し
    html = html.replace(/\n+(\[H3\])/gi, '$1');
    html = html.replace(/(\[\/H3\])\n+/gi, '$1');
    // H4見出し
    html = html.replace(/\n+(\[H4\])/gi, '$1');
    html = html.replace(/(\[\/H4\])\n+/gi, '$1');

    // [H4]の閉じタグ忘れを補完
    // [H4]で始まり[/H4]で閉じられていない場合、最初の改行の前に[/H4]を挿入
    html = html.replace(/\[H4\]([^\n]*?)(\n|$)/g, (match, content, lineEnd) => {
      // すでに[/H4]が含まれている場合はそのまま
      if (content.includes('[/H4]')) {
        return match;
      }
      // 改行の前に[/H4]を追加
      return `[H4]${content}[/H4]${lineEnd}`;
    });

    // [H4]閉じタグ直後の改行も削除
    html = html.replace(/(\[\/H4\])\s*\n/gi, '$1');

    // 見出し [H2]...[/H2], [H3]...[/H3], [H4]...[/H4] → <h2>...</h2>, <h3>...</h3>, <h4>...</h4>
    // Ant Design Tag preset colors: green (#f6ffed background, #389e0d text)
    html = html.replace(
      /\[H2\](.*?)\[\/H2\]/g,
      '<h2 style="margin: 20px 0 12px; font-size: 20px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #fa8c16; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H2</span><span>$1</span></h2>',
    );
    html = html.replace(
      /\[H3\](.*?)\[\/H3\]/g,
      '<h3 style="margin: 16px 0 10px; font-size: 18px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #faad14; color: white; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H3</span><span>$1</span></h3>',
    );
    html = html.replace(
      /\[H4\](.*?)\[\/H4\]/g,
      '<h4 style="margin: 2em 0 1em; font-size: 16px; font-weight: 600; color: #1d1d1d; display: flex; align-items: center; gap: 8px;"><span style="display: inline-block; background: #f6ffed; border: 1px solid #b7eb8f; color: #389e0d; font-size: 11px; padding: 2px 8px; border-radius: 999px; line-height: 20px;">H4</span><span>$1</span></h4>',
    );

    // 箇条書き [UL][LI]...[/LI][/UL] → <ul><li>...</li></ul>
    // 🔧 [\s\S] を使用して改行を含むコンテンツにマッチ
    html = html.replace(/\[UL\]([\s\S]*?)\[\/UL\]/g, (match, content) => {
      const items = content.match(/\[LI\]([\s\S]*?)\[\/LI\]/g);
      if (!items) return match;
      const liTags = items
        .map(item => item.replace(/\[LI\]([\s\S]*?)\[\/LI\]/, '<li>$1</li>'))
        .join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${liTags}</ul>`;
    });

    // 表 [TABLE][TR][TH]...[/TH][TD]...[/TD][/TR][/TABLE] → <table>
    // 🔧 [\s\S] を使用して改行を含むコンテンツにマッチ
    html = html.replace(/\[TABLE\]([\s\S]*?)\[\/TABLE\]/g, (match, content) => {
      const rows = content.match(/\[TR\]([\s\S]*?)\[\/TR\]/g);
      if (!rows) return match;
      const trTags = rows
        .map(row => {
          let cells = row.replace(/\[TR\]/, '').replace(/\[\/TR\]/, '');
          // [TH]...[/TH] → <th>...</th>
          cells = cells.replace(
            /\[TH\]([\s\S]*?)\[\/TH\]/g,
            '<th style="border: 1px solid #e8e8e8; padding: 12px; background: #fafafa; font-weight: 600;">$1</th>',
          );
          // [TD]...[/TD] → <td>...</td>
          cells = cells.replace(
            /\[TD\]([\s\S]*?)\[\/TD\]/g,
            '<td style="border: 1px solid #e8e8e8; padding: 12px;">$1</td>',
          );
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<table style="width: 100%; border-collapse: collapse; margin: 2em 0 1em;">${trTags}</table>`;
    });

    // 太字 [B]...[/B] → <strong>...</strong>
    html = html.replace(/\[B\](.*?)\[\/B\]/g, '<strong>$1</strong>');

    // 改行を<br/>に変換
    html = html.replace(/\n/g, '<br/>');

    // HTMLタグ直後の不要な<br/>を削除
    // 見出し、テーブル、箇条書き直後の<br/>を削除
    html = html.replace(/(<\/h[2-4]>)<br\s*\/?>/gi, '$1');
    html = html.replace(/(<\/ul>)<br\s*\/?>/gi, '$1');
    html = html.replace(/(<\/table>)<br\s*\/?>/gi, '$1');

    // マークダウン太字（後方互換性のため残す） **text** → <strong>text</strong>
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // 引用番号 [n] をクリッカブルな<sup>に変換
    html = html.replace(
      /\[(\d+)\]/g,
      '<sup class="step4-citation-number" data-citation-id="$1" style="cursor: pointer; color: #1890ff; font-weight: 600; text-decoration: underline;">[$1]</sup>',
    );

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // URLからドメインを抽出
  const extractDomain = url => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (e) {
      return url;
    }
  };

  // 目次データを抽出
  const extractTableOfContents = () => {
    const toc = [];
    const processedIds = new Set(); // 処理済みIDを記録

    // workingDraftから基本の見出し構造を取得（これがメインの目次）
    if (!workingDraft?.sections) {
      return [];
    }

    workingDraft.sections.forEach((section, sectionIndex) => {
      // H2見出し
      toc.push({
        id: section.id,
        level: 'h2',
        text: section.title || section.text || '(無題)',
        isDynamic: false,
      });
      processedIds.add(section.id);

      // H3サブセクション（workingDraftにあるもの）
      // H4と独自情報（h3_child, h4_child）は目次に含めない
      const subsections = section.subsections || [];
      subsections.forEach((subsection, subIndex) => {
        // H4見出しと独自情報の子要素（└パターン）をスキップ
        const shouldSkip =
          subsection.type === 'h4' ||
          subsection.placementLevel === 'h4' ||
          subsection.placementLevel === 'h4_child' ||
          subsection.placementLevel === 'h3_child'; // 独自情報の└パターンのみスキップ

        if (shouldSkip) {
          return; // 目次に含めない
        }

        toc.push({
          id: subsection.id,
          level: 'h3',
          text: subsection.title || subsection.text || '(無題)',
          isDynamic: false,
        });
        processedIds.add(subsection.id);
      });

      // 動的に生成されたサブセクション（generatedContentsから検出）
      // H4は除外（heading_level === 'h4'）
      const dynamicSubsections = generatedContents.filter(content => {
        // このセクションの動的サブセクションかどうかをチェック
        // 例: jgia6thk_h3_1 の親は jgia6thk
        if (processedIds.has(content.heading_id)) {
          return false; // 既に処理済み
        }

        // heading_idに親IDが含まれているかチェック
        // パターン: {parent_id}_h3_{number} または {parent_id}_h4_{number}
        const idParts = content.heading_id.split('_');
        if (idParts.length >= 3) {
          const possibleParentId = idParts[0];
          return possibleParentId === section.id;
        }

        return false;
      });

      dynamicSubsections.forEach((dynamicSub, dynIndex) => {
        // H4は目次に含めない
        const isH4 = dynamicSub.heading_level === 'h4';

        if (!processedIds.has(dynamicSub.heading_id) && !isH4) {
          toc.push({
            id: dynamicSub.heading_id,
            level: dynamicSub.heading_level || 'h3',
            text:
              dynamicSub.heading_text ||
              dynamicSub.heading_title ||
              '(動的生成)',
            isDynamic: true,
          });
          processedIds.add(dynamicSub.heading_id);
        }
      });
    });

    return toc;
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Render: Statistics Block
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const renderStatistics = () => {
    if (generatedContents.length === 0) return null;

    return (
      <Card size="small" style={{ marginBottom: 24 }} className="step4-2-stats">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space size="large">
            <div>
              <Text type="secondary">総見出し数</Text>
              <Title level={4} style={{ margin: 0 }}>
                {stats.total_headings || 0}
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 40 }} />
            <div>
              <Text type="secondary">成功</Text>
              <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                {stats.success_count || 0}
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 40 }} />
            <div>
              <Text type="secondary">失敗</Text>
              <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                {stats.failed_count || 0}
              </Title>
            </div>
            <Divider type="vertical" style={{ height: 40 }} />
            <div>
              <Text type="secondary">総文字数</Text>
              <Title level={4} style={{ margin: 0 }}>
                {(stats.total_word_count || 0).toLocaleString()}
              </Title>
            </div>
          </Space>

          {/* 再生成ボタン（管理者のみ、閲覧専用時は非表示） */}
          {!isReadOnly &&
            !generating &&
            (userRole === 'admin' || userRole === 'master') && (
              <Button
                icon={<ReloadOutlined />}
                onClick={onStartGeneration}
                size="small"
              >
                再生成
              </Button>
            )}
        </div>
      </Card>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Render: Skeleton Sections (未完了セクション)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const renderSkeletonSections = () => {
    if (!generating || !generationStarted) return null;

    const completedCount = generatedContents.filter(
      c => c.heading_id !== '__intro__',
    ).length;
    const remainingCount = Math.max(totalHeadings - completedCount, 0);

    if (remainingCount === 0) return null;

    return Array.from({ length: remainingCount }).map((_, index) => (
      <div key={`skeleton-${index}`} style={{ marginBottom: 48 }}>
        <Skeleton.Input
          active
          style={{ width: 200, height: 32, marginBottom: 16 }}
        />
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    ));
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Render: Skeleton Citations (参考データ)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const renderCitationsSkeleton = () => {
    return Array.from({ length: 10 }).map((_, index) => (
      <div
        key={`citation-skeleton-${index}`}
        style={{
          padding: '16px 12px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    ));
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Render: Article Content
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const renderArticleContent = () => {
    // 生成中かつまだコンテンツがない場合はスケルトンを表示
    if (generating && generatedContents.length === 0) {
      return (
        <div>
          <Skeleton
            active
            paragraph={{ rows: 2 }}
            style={{ marginBottom: 48 }}
          />
          {renderSkeletonSections()}
        </div>
      );
    }

    if (generatedContents.length === 0) {
      if (!isReadOnly) {
        return (
          <div style={{ textAlign: 'center', margin: '80px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#8c8c8c' }}>
              生成を準備中...
            </div>
          </div>
        );
      }
      return (
        <Empty
          description="本文はまだ生成されていません"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '80px 0' }}
        />
      );
    }

    const articleTitle =
      workingDraft?.title || articleData?.title || '無題の記事';

    // リード文と本文を分離
    const introContent = generatedContents.find(
      c => c.heading_id === '__intro__',
    );
    const mainContents = generatedContents.filter(
      c => c.heading_id !== '__intro__',
    );

    return (
      <div>
        {/* 記事タイトル */}
        <Title level={2} style={{ marginBottom: 32, fontSize: 28 }}>
          {articleTitle}
        </Title>

        {/* リード文 */}
        {introContent && introContent.content && (
          <div
            id="__intro__"
            className="content-section-fadeIn"
            style={{
              marginBottom: 48,
              scrollMarginTop: '80px',
            }}
          >
            {/* 本文 */}
            <div
              style={{
                fontSize: '15px',
                lineHeight: '1.8',
              }}
            >
              {renderMarkdown(introContent.content, introContent.heading_id)}
            </div>

            {/* 完了マーク + 文字数 */}
            <div style={{ marginTop: 16 }}>
              <Space size={16}>
                <Tag
                  icon={<CheckCircleOutlined />}
                  color="blue"
                  style={{ fontSize: 12 }}
                >
                  生成完了
                </Tag>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {introContent.word_count || 0} 文字
                </Text>
              </Space>
            </div>

            {/* 区切り線 */}
            <div
              style={{
                marginTop: 16,
                borderTop: '1px solid #f0f0f0',
              }}
            />
          </div>
        )}

        {/* 各見出しの本文 */}
        {mainContents.map((content, index) => {
          // heading_levelを正しく取得（h2, h3, h4）
          const level = (content.heading_level || 'h2').toLowerCase();
          const headingText = content.heading_text || content.heading_title;
          const isNew = newContentIds.has(content.heading_id);

          return (
            <div
              key={content.heading_id || index}
              id={content.heading_id}
              className={
                isNew ? 'content-section-new' : 'content-section-fadeIn'
              }
              style={{
                marginBottom: 48,
                scrollMarginTop: '80px', // スクロール時にヘッダーと重ならないように
              }}
            >
              {/* 見出し */}
              <div style={{ marginBottom: 16 }}>
                <Space size={8} align="center">
                  <Tag
                    color={
                      level === 'h2'
                        ? 'orange'
                        : level === 'h3'
                          ? 'yellow'
                          : 'green'
                    }
                    style={{ borderRadius: 999 }}
                  >
                    {level.toUpperCase()}
                  </Tag>
                  <Title
                    level={level === 'h2' ? 3 : level === 'h3' ? 4 : 5}
                    style={{ margin: 0 }}
                  >
                    {headingText}
                  </Title>
                </Space>
              </div>

              {/* 本文 */}
              {content.failed ? (
                <div
                  style={{
                    padding: '16px',
                    background: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '4px',
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      <Text type="danger">
                        {content.error || '生成に失敗しました'}
                      </Text>
                    </Space>
                    {!isReadOnly && (
                      <Button
                        type="primary"
                        danger
                        icon={<ReloadOutlined />}
                        size="small"
                        onClick={async () => {
                          try {
                            message.loading({
                              content: '再生成を開始しています...',
                              key: 'retry',
                            });
                            await v2ArticlesApi.retrySection(
                              articleUuid,
                              content.heading_id,
                            );
                            message.success({
                              content: 'セクションの再生成を開始しました',
                              key: 'retry',
                            });
                            // 親コンポーネントの更新をトリガー
                            if (onLoadContent) {
                              setTimeout(() => onLoadContent(), 2000);
                            }
                          } catch (err) {
                            message.error({
                              content: `再生成に失敗しました: ${err.message}`,
                              key: 'retry',
                            });
                          }
                        }}
                      >
                        このセクションを再生成
                      </Button>
                    )}
                  </Space>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      lineHeight: 1.8,
                      fontSize: 15,
                      color: '#333',
                    }}
                  >
                    {/* パーツモード・通常モード共にMarkdown処理を適用 */}
                    {/* 独自情報はMarkdown形式で保存されているため、renderMarkdownで変換 */}
                    {renderMarkdown(content.content, content.heading_id)}
                  </div>

                  {/* 完了マーク + 文字数 */}
                  <div style={{ marginTop: 16 }}>
                    <Space size={16}>
                      <Tag
                        icon={<CheckCircleOutlined />}
                        color="blue"
                        style={{ fontSize: 12 }}
                      >
                        生成完了
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {content.word_count || 0} 文字
                      </Text>
                    </Space>
                  </div>

                  {/* 区切り線 */}
                  <div
                    style={{
                      marginTop: 16,
                      borderTop: '1px solid #f0f0f0',
                    }}
                  />
                </>
              )}
            </div>
          );
        })}

        {/* スケルトン表示（未完了セクション） */}
        {renderSkeletonSections()}
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Render: Right Panel (Reference Data / Table of Contents)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const renderRightPanel = () => {
    // 生成中でコンテンツがない場合もパネルを表示
    if (generatedContents.length === 0 && !generating) return null;

    const citationCount = citations.length;
    const toc = extractTableOfContents();

    return (
      <div
        style={{
          position: 'sticky',
          top: 24,
          maxHeight: 'calc(100vh - 48px)',
          background: '#fafafa',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Segmented for switching between reference and TOC */}
        <div style={{ marginBottom: 16, flexShrink: 0 }}>
          <Segmented
            value={rightPanelView}
            onChange={value => setRightPanelView(value)}
            options={[
              {
                label: '目次',
                value: 'toc',
                icon: <UnorderedListOutlined />,
              },
              {
                label: `参考データ (${citationCount})`,
                value: 'reference',
                icon: <LinkOutlined />,
              },
            ]}
            block
          />
        </div>

        {/* スクロール可能なボディ部分 */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Reference Data View */}
          {rightPanelView === 'reference' && (
            <div>
              {/* 検索ボックス */}
              {citations.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Input
                    placeholder="参考データを検索..."
                    prefix={<SearchOutlined />}
                    value={citationSearchQuery}
                    onChange={e => setCitationSearchQuery(e.target.value)}
                    allowClear
                    style={{ borderRadius: 6 }}
                  />
                </div>
              )}

              {generating && citations.length === 0 ? (
                // 生成中でまだ引用データがない場合はスケルトン表示
                <div>{renderCitationsSkeleton()}</div>
              ) : citations.length === 0 ? (
                <Empty
                  description="参考データなし"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div>
                  {/* citation_idでソート、検索フィルター適用 */}
                  {[...citations]
                    .filter(citation => {
                      if (!citationSearchQuery) return true;
                      const query = citationSearchQuery.toLowerCase();
                      const content = (
                        citation.content ||
                        citation.text ||
                        ''
                      ).toLowerCase();
                      return content.includes(query);
                    })
                    .sort((a, b) => (a.citation_id || 0) - (b.citation_id || 0))
                    .map((citation, index) => {
                      // provided_infoかどうかをチェック（source_url === "provided_info" または URLがない）
                      const isProvidedInfo =
                        citation.source_url === 'provided_info' ||
                        citation.url === 'provided_info' ||
                        (!citation.url &&
                          !citation.source_url &&
                          !citation.link);

                      // URLの取得（provided_infoの場合は除外）
                      const citationUrl =
                        !isProvidedInfo &&
                        (citation.url ||
                          citation.source_url ||
                          citation.link ||
                          '');

                      // テキストを100文字で切る
                      const fullText = citation.content || citation.text || '';
                      const truncatedText =
                        fullText.length > 100
                          ? fullText.substring(0, 100) + '…'
                          : fullText;

                      return (
                        <div
                          key={citation.citation_id || index}
                          id={`citation-${citation.citation_id || citation.id}`}
                          className={
                            highlightedCitationId ===
                            (citation.citation_id || citation.id)
                              ? 'citation-card-highlight'
                              : 'citation-card-normal'
                          }
                          style={{
                            padding: '16px 12px',
                            background: '#fff',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                        >
                          {/* 引用番号 + エビデンステキスト */}
                          <div
                            style={{
                              display: 'flex',
                              gap: 12,
                              marginBottom: 8,
                            }}
                          >
                            {/* 左側：引用番号 */}
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: '600',
                                color: '#8c8c8c',
                                flexShrink: 0,
                                minWidth: 20,
                              }}
                            >
                              {citation.citation_id || citation.id}:
                            </div>

                            {/* 右側：エビデンステキスト */}
                            {(citation.content || citation.text) && (
                              <div
                                style={{
                                  flex: 1,
                                  fontSize: 13,
                                  lineHeight: 1.6,
                                  color: '#333',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: truncatedText.replace(
                                    /\*\*(.*?)\*\*/g,
                                    '<strong style="font-weight: 500;">$1</strong>',
                                  ),
                                }}
                              />
                            )}
                          </div>

                          {/* ドメイン（右揃え・リンク）またはprovided_infoラベル */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              marginTop: 8,
                            }}
                          >
                            {isProvidedInfo ? (
                              <Tag color="purple" style={{ margin: 0 }}>
                                独自情報
                              </Tag>
                            ) : citationUrl ? (
                              <a
                                href={citationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="citation-domain-link"
                                style={{
                                  fontSize: 12,
                                  color: '#8c8c8c',
                                  textDecoration: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  transition: 'color 0.2s ease',
                                }}
                              >
                                <LinkOutlined style={{ fontSize: 11 }} />
                                <span>{extractDomain(citationUrl)}</span>
                              </a>
                            ) : (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: '#bfbfbf',
                                  fontStyle: 'italic',
                                }}
                              >
                                ソース不明
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Table of Contents View */}
          {rightPanelView === 'toc' && (
            <div>
              {toc.length === 0 ? (
                <Empty
                  description="目次なし"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <div>
                  {toc
                    .filter(item => item.level === 'h2' || item.level === 'h3')
                    .map((item, index) => {
                      const isActive = item.id === activeHeadingId;
                      const isH2 = item.level === 'h2';
                      const isH3 = item.level === 'h3';

                      return (
                        <div
                          key={item.id || index}
                          style={{
                            marginBottom: 2,
                            paddingLeft: isH2 ? 0 : isH3 ? 16 : 32,
                          }}
                        >
                          <a
                            href={`#${item.id}`}
                            onClick={e => {
                              e.preventDefault();
                              const element = document.getElementById(item.id);
                              if (element) {
                                element.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start',
                                });
                              }
                            }}
                            style={{
                              fontSize: isH2 ? 14 : 13,
                              fontWeight: isH2
                                ? '500'
                                : isH3
                                  ? '500'
                                  : 'normal',
                              color: '#333', // 動的サブセクションも通常と同じ色
                              textDecoration: 'none',
                              display: 'block',
                              padding: isH2 ? '8px 12px' : '6px 12px',
                              borderRadius: '4px',
                              backgroundColor: isActive
                                ? '#eaf8ff'
                                : 'transparent',
                              transition: 'all 0.2s',
                              cursor: 'pointer',
                              fontStyle: 'normal', // 動的サブセクションも通常と同じフォントスタイル
                            }}
                            onMouseEnter={e => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor =
                                  '#f2f2f2';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor =
                                  'transparent';
                              }
                            }}
                          >
                            {item.text}
                          </a>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Tourステップ定義
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const tourSteps = React.useMemo(() => {
    if (tourLoading || !TOUR_CONFIGS.generation_step4_2) return [];

    const allSteps = TOUR_CONFIGS.generation_step4_2.steps.map(step => {
      // tourConfigsで定義されたtarget関数をそのまま使う
      return {
        ...step,
        // target関数が既に定義されている場合はそのまま、なければnull
        target: step.target || null,
      };
    });

    return allSteps;
  }, [tourLoading, tourOpen]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // Main Render
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  return (
    <div>
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="STEP4.2: 本文生成"
        />
      )}

      <div style={{ padding: '24px' }}>
        {/* 統計情報ブロック */}
        {renderStatistics()}

        {/* メインコンテンツ：左カラム（本文）+ 右カラム（参考データ/目次） */}
        <Row gutter={24} className="step4-2-layout">
          {/* 左カラム：記事本文 */}
          <Col span={16}>{renderArticleContent()}</Col>

          {/* 右カラム：参考データ/目次 */}
          <Col span={8}>{renderRightPanel()}</Col>
        </Row>

        {/* フッターボタン - 生成完了後のみ表示 */}
        {generatedContents.length > 0 && !generating && (
          <div style={{ marginTop: 32 }}>
            <Button
              type="primary"
              onClick={() => onCompleted && onCompleted(generatedContents)}
              disabled={isReadOnly}
              size="large"
              block
            >
              ブラッシュアップへ進む →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step4_2_ContentGeneration;
